/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { projectService } from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';

interface ProjectResourceSummary {
  containers?: number;
  nfs_storages?: number;
  buckets?: number;
  vms?: number;
  total?: number;
}

interface DeleteProjectErrorBody {
  error?: string;
  resources?: ProjectResourceSummary;
}

function describeResources(r: ProjectResourceSummary): string {
  const parts: string[] = [];
  if (r.containers) parts.push(`${r.containers} pod${r.containers === 1 ? '' : 's'}`);
  if (r.nfs_storages) parts.push(`${r.nfs_storages} NFS volume${r.nfs_storages === 1 ? '' : 's'}`);
  if (r.buckets) parts.push(`${r.buckets} bucket${r.buckets === 1 ? '' : 's'}`);
  if (r.vms) parts.push(`${r.vms} VM${r.vms === 1 ? '' : 's'}`);
  return parts.join(', ');
}

export const useProjects = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();

  const { data: projectsData, isLoading, isPending, isError, refetch, isFetched } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.list().then((res) => res.data),
    enabled: isAuthenticated && !authLoading,
  });

  // Handle both response formats: { projects: [...] } or just [...]
  const projects = projectsData?.projects || (Array.isArray(projectsData) ? projectsData : []);

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description: string }) =>
      projectService.create(data.name, data.description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project created successfully');
    },
    onError: () => {
      toast.error('Failed to create project');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; description: string } }) =>
      projectService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project updated successfully');
    },
    onError: () => {
      toast.error('Failed to update project');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project deleted successfully');
    },
    onError: (error: AxiosError<DeleteProjectErrorBody>) => {
      if (error.response?.status === 409 && error.response.data?.resources) {
        const detail = describeResources(error.response.data.resources);
        toast.error(
          detail
            ? `Project still has active resources: ${detail}. Delete them first, then retry.`
            : (error.response.data.error || 'Project still has active resources. Delete them first, then retry.'),
          { duration: 7000 },
        );
        return;
      }
      if (error.response?.status === 503) {
        toast.error(error.response.data?.error || 'Could not verify project resources; please retry.');
        return;
      }
      toast.error(error.response?.data?.error || 'Failed to delete project');
    },
  });

  return {
    projects,
    projectsData,
    isLoading: isLoading || isPending,
    isFetched,
    isError,
    refetch,
    createProject: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateProject: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteProject: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
